import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/site/Seo";
 
const CATEGORIES = ["All", "Design Industry", "Research", "Brand", "Membership"];
 
const POSTS = [
  {
    slug: "superside-alternatives",
    featured: true,
    category: "Design Industry",
    title: "The best Superside alternatives in 2026",
    subtitle: "Superside raised prices, cut staff, and pivoted to AI. An honest breakdown of every real alternative — and who each one is right for.",
    excerpt: "Superside raised prices, cut staff, and pivoted to AI. An honest breakdown of every real alternative — and who each one is right for.",
    cover: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1200&q=80",
    readTime: "9 min read",
    date: "2026-07-09",
    author: { name: "Azam Tariq" },
  },
  {
    slug: "design-subscription-vs-agency",
    featured: false,
    category: "Research",
    title: "Design subscription vs agency vs in-house: the real cost",
    subtitle: "We ran the numbers on all three models. The answer is not what most founders expect.",
    excerpt: "We ran the numbers on all three models. The answer is not what most founders expect.",
    cover: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80",
    readTime: "12 min read",
    date: "2026-08-01",
    author: { name: "Azam Tariq" },
    soon: true,
  },
  {
    slug: "state-of-design-subscriptions-2026",
    featured: false,
    category: "Research",
    title: "The state of design subscriptions 2026",
    subtitle: "We read 1,200 reviews across Trustpilot, G2, and Clutch. The same seven problems show up at every price point.",
    excerpt: "We read 1,200 reviews across Trustpilot, G2, and Clutch. The same seven problems show up at every price point.",
    cover: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    readTime: "15 min read",
    date: "2026-08-15",
    author: { name: "Azam Tariq" },
    soon: true,
  },
];
 
const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};
 
const initials = (name) =>
  (name || "").split(" ").filter(Boolean).map((s) => s[0]).join("").toUpperCase();
 
export default function Blog() {
  const [active, setActive] = useState("All");
 
  const featured = useMemo(() => POSTS.find((p) => p.featured), []);
  const rest = useMemo(() => POSTS.filter((p) => !p.featured), []);
  const filtered = useMemo(
    () => (active === "All" ? rest : rest.filter((p) => p.category === active)),
    [active, rest]
  );
 
  return (
    <div data-testid="blog-index" style={{ background: "#f5f1ea" }}>
      <Seo
        title="Journal — Design Industry Research | Midcreatives"
        description="Field notes on brand, design and building the room — long-form thinking from Midcreatives and the design leaders in our community."
        path="/blog"
      />
 
      {/* Hero */}
      <section>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-10 md:pt-16 pb-12 md:pb-16">
          <p className="font-mono-mc mb-6" style={{ color: "#9a948a" }}>Field notes · The blog</p>
          <h1 className="font-display-tight text-5xl sm:text-7xl lg:text-[104px] leading-[0.92] max-w-6xl" style={{ color: "#0a0a0a" }}>
            Field notes on brand, design{" "}
            <span style={{ color: "rgba(10,10,10,0.4)" }}>and building the room.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl leading-relaxed max-w-3xl" style={{ color: "rgba(10,10,10,0.65)" }}>
            Long-form thinking from the Midcreatives team and the design leaders in our community. No hot takes, no listicles — just the frameworks we actually use.
          </p>
        </div>
      </section>
 
      {/* Filter tabs — sticky */}
      <section style={{ background: "rgba(245,241,234,0.85)", backdropFilter: "blur(12px)", borderTop: "1px solid rgba(10,10,10,0.1)", borderBottom: "1px solid rgba(10,10,10,0.1)", position: "sticky", top: "80px", zIndex: 40 }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-4 flex items-center gap-2 md:gap-3 overflow-x-auto">
          {CATEGORIES.map((c) => {
            const isActive = c === active;
            const count = c === "All" ? rest.length : rest.filter((p) => p.category === c).length;
            return (
              <button
                key={c}
                onClick={() => setActive(c)}
                style={{
                  whiteSpace: "nowrap",
                  padding: "8px 16px",
                  borderRadius: "999px",
                  fontSize: "14px",
                  fontWeight: 500,
                  border: isActive ? "1px solid #0a0a0a" : "1px solid rgba(10,10,10,0.15)",
                  background: isActive ? "#0a0a0a" : "rgba(255,255,255,0.6)",
                  color: isActive ? "#ffffff" : "rgba(10,10,10,0.7)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontFamily: "inherit",
                }}
              >
                {c}
                {c !== "All" && count > 0 && (
                  <span style={{ marginLeft: "8px", fontSize: "10px", opacity: 0.6, fontFamily: "'Geist Mono', monospace" }}>
                    {String(count).padStart(2, "0")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>
 
      {/* Featured post */}
      {featured && (
        <section>
          <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-12 md:py-16">
            <Link
              to={`/blog/${featured.slug}`}
              className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center"
              style={{ textDecoration: "none" }}
            >
              <div className="md:col-span-7 rounded-3xl overflow-hidden aspect-[16/10]" style={{ border: "1px solid rgba(10,10,10,0.1)" }}>
                <img
                  src={featured.cover}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-[1400ms]"
                  loading="lazy"
                />
              </div>
              <div className="md:col-span-5">
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 12px", borderRadius: "999px", fontSize: "12px", fontWeight: 500, background: "#0a0a0a", color: "#ffffff" }}>
                    Featured
                  </span>
                  <span className="font-mono-mc" style={{ color: "#9a948a" }}>{featured.category}</span>
                  <span className="font-mono-mc" style={{ color: "#9a948a" }}>·</span>
                  <span className="font-mono-mc" style={{ color: "#9a948a" }}>{featured.readTime}</span>
                </div>
                <h2 className="font-display-tight text-3xl md:text-5xl lg:text-6xl leading-[0.98] group-hover:opacity-80 transition-opacity" style={{ color: "#0a0a0a", marginTop: "24px" }}>
                  {featured.title}
                </h2>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(10,10,10,0.65)", marginTop: "20px" }}>
                  {featured.subtitle}
                </p>
                <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#0a0a0a", color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontFamily: "'Geist Mono', monospace" }}>
                    {initials(featured.author.name)}
                  </div>
                  <div>
                    <p style={{ fontSize: "14px", color: "#0a0a0a", margin: 0 }}>{featured.author.name}</p>
                    <p style={{ fontSize: "12px", color: "rgba(10,10,10,0.5)", margin: 0 }}>{formatDate(featured.date)}</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}
 
      {/* Post grid */}
      <section style={{ paddingBottom: "96px" }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {filtered.map((p) => (
              p.soon ? (
                <div key={p.slug} style={{ opacity: 0.45 }}>
                  <PostCard p={p} />
                </div>
              ) : (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group flex flex-col" style={{ textDecoration: "none" }}>
                  <PostCard p={p} linked />
                </Link>
              )
            ))}
          </motion.div>
 
          {filtered.length === 0 && (
            <p style={{ textAlign: "center", color: "rgba(10,10,10,0.5)", padding: "64px 0", fontFamily: "'Geist Mono', monospace", fontSize: "13px" }}>
              No posts in this category yet.
            </p>
          )}
        </div>
      </section>
 
      {/* Newsletter CTA */}
      <section style={{ paddingBottom: "96px" }}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="rounded-[32px] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end" style={{ background: "#efeae0" }}>
            <div className="md:col-span-8">
              <p className="font-mono-mc mb-4" style={{ color: "#9a948a" }}>Field notes · Monthly</p>
              <h2 className="font-display-tight text-4xl md:text-6xl leading-[1.0]" style={{ color: "#0a0a0a" }}>
                One long-form essay a month.{" "}
                <span style={{ color: "rgba(10,10,10,0.4)" }}>No sales.</span>
              </h2>
              <p className="leading-relaxed max-w-xl" style={{ color: "rgba(10,10,10,0.65)", marginTop: "24px" }}>
                We publish once a month. It is the piece we actually want you to read. Enter your email and we will send it.
              </p>
            </div>
            <div className="md:col-span-4">
              <form onSubmit={(e) => e.preventDefault()} style={{ display: "flex", gap: "8px" }}>
                <input
                  type="email"
                  placeholder="you@company.com"
                  style={{ flex: 1, padding: "12px 16px", borderRadius: "999px", background: "#ffffff", border: "1px solid rgba(10,10,10,0.15)", color: "#0a0a0a", fontSize: "14px", outline: "none", fontFamily: "inherit" }}
                />
                <button
                  type="submit"
                  style={{ padding: "12px 20px", borderRadius: "999px", background: "#0a0a0a", color: "#ffffff", fontWeight: 500, border: "none", cursor: "pointer", fontSize: "14px", fontFamily: "inherit" }}
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
 
function PostCard({ p, linked }) {
  return (
    <>
      <div className="rounded-2xl overflow-hidden aspect-[16/10] mb-5" style={{ border: "1px solid rgba(10,10,10,0.1)" }}>
        <img
          src={p.cover}
          alt={p.title}
          className={`w-full h-full object-cover transition-transform duration-[1200ms] ${linked ? "group-hover:scale-105" : ""}`}
          loading="lazy"
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span className="font-mono-mc" style={{ color: "#9a948a" }}>{p.category}</span>
        <span className="font-mono-mc" style={{ color: "#9a948a" }}>·</span>
        <span className="font-mono-mc" style={{ color: "#9a948a" }}>{p.readTime}</span>
        {p.soon && <span style={{ marginLeft: "auto", fontSize: "11px", color: "#9a948a", fontFamily: "'Geist Mono', monospace" }}>Coming soon</span>}
      </div>
      <h3 className={`font-display-tight text-2xl md:text-3xl leading-[1.05] mt-3 ${linked ? "group-hover:opacity-80" : ""} transition-opacity`} style={{ color: "#0a0a0a" }}>
        {p.title}
      </h3>
      <p className="text-sm md:text-base leading-relaxed flex-1 mt-3" style={{ color: "rgba(10,10,10,0.6)" }}>
        {p.excerpt}
      </p>
      <div style={{ marginTop: "20px", display: "flex", alignItems: "center", gap: "12px", paddingTop: "16px", borderTop: "1px solid rgba(10,10,10,0.1)" }}>
        <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#0a0a0a", color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontFamily: "'Geist Mono', monospace", flexShrink: 0 }}>
          {p.author.name.split(" ").map((s) => s[0]).join("")}
        </div>
        <span style={{ fontSize: "12px", color: "rgba(10,10,10,0.6)" }}>{p.author.name}</span>
        <span style={{ fontSize: "12px", color: "rgba(10,10,10,0.4)", marginLeft: "auto" }}>{formatDate(p.date)}</span>
      </div>
    </>
  );
}